import { Fragment, useState } from 'react'
import { Badge, Button, CopyButton, Input, Textarea } from '@/components/ui'
import { RefUploader } from '@/components/RefUploader'
import type { BatchRow, ParsedRow } from '@/config/batchCsv'

/**
 * ===========================================================================
 * 批量行表格 —— **批量出图工作台（`/batch`）的正身**
 * ===========================================================================
 *
 * 它长什么样，是被两件事推出来的：
 *
 * 1. **它就是页面，不再是抽屉里的一块。** 上一版批量是详情页里的一个输入形态，
 *    宽表按「宽视图 = 弹层」放进了 `Drawer` —— 那是对的：详情页的活是
 *    「填一个产品然后提交」，9 列宽表不该占着它的版面。
 *    但批量搬成独立工作台之后（2026-09-29 用户口径：批量要能后补图片、
 *    关掉页面回来接着改），**这张表就是那一页要干的事**，把它再塞进抽屉
 *    等于每次打开都多点一次。铁律 5 的前提不成立了，所以形态跟着变。
 *
 * 2. **不加页签、不分左右栏**。这张表是一张**宽表**（2026-10-07 起是 4 列），
 *    塞进左栏就是一条横向滚动条。
 *    2026-09-30 之后版面是「参数摘要 → 导入 + 表 → 生成」一条直线（都在左栏），
 *    右栏只剩任务面板。
 *
 * 3. **提示词从这个组件外面注进来**（`promptsFor`）。
 *    逐行提示词必须与单个模式**同一份**组装逻辑，所以这里只负责「什么时候调」，
 *    不负责「怎么拼」。若在这儿自己拼一段，批量与单个就会各出各的词 ——
 *    而这两条路本来就该产出一模一样的东西，只是产品数量不同。
 *
 * 4. **参考图那一格用的是全站唯一的 `RefUploader`（cell 形态）**。
 *    用户口径：「参考图……可以提供图片上传」。之所以不在这儿手写一个小上传器：
 *    上限、去重、主图顺序、裂图退本地副本、上传通道这五件事各抄一遍，
 *    必然抄漏一件 —— 而漏了在截图上看不出来（「没渲染 ≠ 报错」）。
 *    `smoke-nav` 有硬断言挡着，见下面那一格的注释。
 *
 * 表格本体复用全站的 `.table` 类，不另画一套表头样式（同一实现只许一份）。
 */
export function BatchTable({
  rows,
  onEdit,
  onRemove,
  promptsFor,
  onImages,
  maxImages = 6,
}: {
  rows: ParsedRow[]
  onEdit: (line: number, patch: Partial<BatchRow>) => void
  onRemove: (line: number) => void
  /** 逐行提示词 —— 与单个模式同一个组装函数，页面注入 */
  promptsFor: (row: BatchRow) => string[]
  /**
   * 整行替换某一行的参考图（增删都由 `RefUploader` 的 onChange 给出）。
   * **不传就只读** —— 「能力内建在控件里，但不强制每个调用方都有」：
   * 传了个没人接的 handler，那一格会是个点了没反应的死控件，那比只读更坏。
   */
  onImages?: (line: number, images: string[]) => void
  /** 每行参考图上限，与单产品那个上传器一致（都是发给上游的参考图） */
  maxImages?: number
}) {
  /*
   * 展开态是**一个集合**，不是单个行号。
   *
   * 上一版是 `useState<number | null>`：点开 A-3 会把 A-1 折叠掉。
   * 用户核对提示词时的真实动作是「对比两行的词有没有串产品名」——
   * 一次只能看一行，等于逼他把词复制出去比，而那正是这个面板想省掉的事。
   */
  const [open, setOpen] = useState<Record<number, true>>({})
  const toggle = (line: number) => setOpen((m) => (m[line] ? omit(m, line) : { ...m, [line]: true }))

  const badCount = rows.filter((r) => !r.ok).length
  const warnCount = rows.filter((r) => r.ok && r.issues.length > 0).length

  return (
    <div className="batch">
      <div className="batch__bar">
        <Badge tone={badCount ? 'warning' : 'success'}>
          {rows.length} 行 · 可用 {rows.length - badCount} / 待修 {badCount}
        </Badge>
        {warnCount ? (
          <span className="text-xs muted">
            其中 {warnCount} 行有提示（不影响出图，但会影响效果）
          </span>
        ) : null}
        <span className="grow" />
        <span className="text-xs muted">改完即时复查，不用重新导入</span>
      </div>

      <div className="table__wrap">
        <table className="table batch__table">
          {/*
            ===================================================================
            2026-10-07：**「行」「状态」「SKU」三列删除**（用户圈着这三列表头
            说「前3列删除，字变小」）。
            ===================================================================

            删的是**列**，不是这三个字段 —— 它们在数据里一个都没少：

              · `data-line` 仍在 `<tr>` 上（探针、断言、归位全靠它定位）；
              · 每行的 `ok` 仍在 `data-ok` 上，待修的行照旧压一条红竖线；
              · `sku` 仍是 `BatchRow` 的字段，仍参与图片文件名归位、报错指路、
                任务标题与 `inputs.sku`（见 `config/batchCsv.ts`）。

            为什么删是对的：这三列都是**给机器看的坐标**，不是用户要填的东西。
            行号是顺序、状态是派生物（`validateRows` 每次重算）、SKU 多数时候
            由 CSV 带进来或按行号自动编号。而它们吃掉的是最值钱的那段宽度 ——
            产品名与卖点被挤到 138px，「示例产品A：硅藻土沥水垫」在框里就断行。

            状态那一列删掉不等于看不见状态：格子下方的提示行还在，表头那条
            「N 行 · 可用 X / 待修 Y」也还在，坏行仍然是整行淡红 + 左侧红竖线。
          */}
          <thead>
            <tr>
              <th>产品名称 *</th>
              <th>卖点 / 文案要点</th>
              {/*
                「类目」「场景倾向」两列随「商品套图」形态于 2026-09-30 下架
                —— 它们只被套图提示词读过。见 `config/batchCsv.ts` 的文件头。
              */}
              <th>参考图</th>
              <th>操作</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => {
              const isOpen = Boolean(open[r.line])
              const list = isOpen ? promptsFor(r.row) : []
              return (
                <Fragment key={r.line}>
                  {/*
                    `data-line` / `data-ok` 是**给断言与归位用的稳定键**，
                    不跟着上面删掉的那三列走 —— 列没了，这两个属性照旧。
                    坏行的淡红底与左侧红竖线的判据也在它们身上（见 global.css）。
                  */}
                  <tr data-ok={r.ok} data-line={r.line}>
                    <td>
                      <Input
                        value={r.row.productName}
                        placeholder="必填"
                        aria-label={`第 ${r.line} 行产品名称`}
                        onChange={(e) => onEdit(r.line, { productName: e.target.value })}
                      />
                    </td>
                    <td>
                      <Textarea
                        rows={2}
                        value={r.row.sellingPoints}
                        placeholder="一行一个卖点"
                        aria-label={`第 ${r.line} 行卖点`}
                        onChange={(e) => onEdit(r.line, { sellingPoints: e.target.value })}
                      />
                    </td>
                    {/*
                      参考图列 = 全站那个 `RefUploader` 的 **cell 形态**
                      （2026-09-30 用户口径：「参考图……可以提供图片上传」，
                      指的是就这一格）。

                      ⚠️ 这里**不许**自己拼一个「文件框 + 缩略图 + 删除」。
                      `smoke-nav` 有一条硬断言：全站任何 tsx 里出现 `accept` 含
                      image 的文件框、且不是 RefUploader，直接判失败 —— 那条断言
                      是被「某页漏了清空 / 上限写死成 4 / 缩略图没接删除」这些
                      历史事故逼出来的。所以是**组件多一种排版**，不是多一个控件：
                      上限、去重、主图边框、裂图退本地副本、上传通道全部同一份。
                    */}
                    <td className="batch__refs">
                      {onImages ? (
                        <RefUploader
                          wrap="cell"
                          urls={r.row.images}
                          onChange={(urls) => onImages(r.line, urls)}
                          max={maxImages}
                          title="参考图"
                          firstBadge=""
                        />
                      ) : (
                        <span className="text-xs muted">
                          {r.row.images.length ? `${r.row.images.length} 张` : '无'}
                        </span>
                      )}
                    </td>
                    <td className="batch__ops">
                      <Button
                        size="sm"
                        variant={isOpen ? 'primary' : 'ghost'}
                        icon="text"
                        onClick={() => toggle(r.line)}
                      >
                        {isOpen ? '收起' : '提示词'}
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        icon="trash"
                        aria-label={`删除第 ${r.line} 行`}
                        disabled={rows.length <= 1}
                        onClick={() => onRemove(r.line)}
                      />
                    </td>
                  </tr>

                  {r.issues.length ? (
                    <tr className="batch__issues">
                      {/* ⚠️ 列数 = 上面表头的 4 个；改表头必须同改这里（两处） */}
                      <td colSpan={4}>
                        <ul className="batch-issues">
                          {r.issues.map((x, i) => (
                            <li key={`${x.field}-${i}`} data-level={x.level}>
                              <span className="batch__lvl">{x.level === 'error' ? '待修' : '提示'}</span>
                              {x.text}
                            </li>
                          ))}
                        </ul>
                      </td>
                    </tr>
                  ) : null}

                  {isOpen ? (
                    <tr className="batch__prompts">
                      {/* ⚠️ 列数 = 上面表头的 4 个；改表头必须同改这里（两处） */}
                      <td colSpan={4}>
                        {list.length ? (
                          <>
                            <div className="batch__prompts-head">
                              <span className="text-xs muted">
                                这一行会出 {list.length} 张，下面是与单个模式同一份逻辑拼出的逐张提示词
                              </span>
                              <span className="grow" />
                              <CopyButton text={list.join('\n\n')} label="复制本行全部" />
                            </div>
                            <ol className="batch__prompts-list">
                              {list.map((p, i) => (
                                <li key={i}>
                                  <div className="batch__prompts-idx">第 {i + 1} / {list.length} 张</div>
                                  <pre className="task__textbody">{p}</pre>
                                </li>
                              ))}
                            </ol>
                          </>
                        ) : (
                          <span className="text-xs muted">
                            这一档暂时拼不出逐张提示词 —— 先选好产出形态与张数。
                          </span>
                        )}
                      </td>
                    </tr>
                  ) : null}
                </Fragment>
              )
            })}
          </tbody>
        </table>
      </div>
    </div>
  )
}

/** 删掉一个键并返回新对象（展开态用） */
function omit<T extends Record<number, true>>(m: T, key: number): T {
  const next = { ...m }
  delete next[key]
  return next
}
